import type { SimEvent } from "../protocol/events";
import type { Composition } from "../protocol/snapshot";
import { addComposition, maxMultiple, scaleComposition, species, subtractComposition, totalUnits } from "./composition";
import { AMBIENT_TEMP_C, newPacket, resolvePropertyRecipe, type EngineNode, type EnginePacket, type EngineState } from "./state";

/**
 * Separation for one processing node (spec 0023, `01-simulation-model.md` §4's
 * "Separators"). Runs inside step 2's node loop, immediately after
 * `processPhaseConversion` — no tick step is added.
 *
 * Unlike phase conversion (a per-packet split, `phase.ts`), separation pools
 * the WHOLE buffer exactly as `reactions.ts`'s `processReactions` does — spec
 * 0023 Open Question 2 (resolved 2026-09-22, human decision): separation
 * scales with `k`, the largest integer multiple of the recipe's `inputs` the
 * pool holds, partitioned in one `scaleComposition` call rather than a loop.
 */
export function processSeparation(state: EngineState, node: EngineNode, events: SimEvent[]): void {
  if (node.isDestroyed || node.kind !== "separator") return;
  // `03-hazards.md` §2.5/§3.3: a sludged or infested node separates at 0.5
  // units/tick too, gated by the same decisions `pushFromNode` reads
  // (`resolveSludgeGate`/`resolveInfestationGate`, tick.ts).
  if (!node.sludgeActiveThisTick || !node.verminActiveThisTick) return;
  if (node.buffer.length === 0) return;

  const recipe = resolvePropertyRecipe(state, node);
  if (recipe === undefined || recipe.primarySpecies === undefined) return;

  // Pool the buffer so the recipe can draw across several arrived packets,
  // same as `processReactions`.
  const pooled = node.buffer.reduce((acc, p) => addComposition(acc, p.composition), {} as Composition);

  // The Limiting Reagent Rule (01-simulation-model.md §4, spec 0008), reused
  // here per spec 0023 Open Question 2: k whole multiples of the recipe
  // fire, not just one.
  const k = maxMultiple(pooled, recipe.inputs);
  if (k === 0) return;

  const partitioned = scaleComposition(recipe.inputs, k);

  const mainComposition: Composition = {};
  const wasteBase: Composition = {};
  for (const key of species(partitioned)) {
    if (recipe.primarySpecies.includes(key)) mainComposition[key] = partitioned[key]!;
    else wasteBase[key] = partitioned[key]!;
  }

  // Whatever the pool held beyond the k multiples consumed is folded into
  // waste, not dropped — mirroring `reactions.ts`'s "mass is never silently
  // dropped" rule for its own remainder. Waste is the separator's catch-all:
  // primary species go down `main`, everything else (including this
  // leftover) goes down `waste` (spec 0023 Intent).
  const remainder = subtractComposition(pooled, partitioned);
  const wasteComposition = addComposition(wasteBase, remainder);

  // No empty fraction gets a packet id (mirrors `phase.ts`'s vapour/liquid
  // rule). Separation replaces the whole buffer with at most 2 packets rather
  // than adding to it, so the capacity guard counts the *result*, not +1.
  const outputCount = (totalUnits(mainComposition) > 0 ? 1 : 0) + (totalUnits(wasteComposition) > 0 ? 1 : 0);
  if (outputCount > node.bufferCapacity) return;

  const isContaminated = node.buffer.some((p) => p.isContaminated);

  // Main before waste, always (spec 0023 Constraints: determinism) — this is
  // id allocation order, so replays match.
  // G-G17: both fractions are born at ambient, not inherited from the feed
  // that fed the separator (spec 0025).
  const mainPacket =
    totalUnits(mainComposition) > 0
      ? newPacket(state, mainComposition, {
          isProduct: true,
          isContaminated,
          separationRole: "main",
          temperature: AMBIENT_TEMP_C,
        })
      : null;
  const wastePacket =
    totalUnits(wasteComposition) > 0
      ? newPacket(state, wasteComposition, {
          isProduct: true,
          isContaminated,
          separationRole: "waste",
          temperature: AMBIENT_TEMP_C,
        })
      : null;

  node.buffer = [mainPacket, wastePacket].filter((p): p is EnginePacket => p !== null);

  events.push({
    type: "packet.separated",
    nodeId: node.id,
    multiple: k,
    mainComposition,
    wasteComposition,
  });
}
